import { Box, Button, TextareaAutosize } from '@mui/material'
import { NodeIdMap, createIdMap } from '../../version_mapping/utility'
import React, { useEffect, useState } from 'react'
import { openNode } from '../../utility/TreeNavigation'
import { INode } from 'shared/types/tree'
import { WithTree } from '../navigationHelpers/WithTree'
import { updateAll } from './utility'
import { getAllUpdates } from './skillsListNoteNode'
import { ConfirmCommitButton } from 'shared/ui'
import { NodeBasedUpdates, SkippedUpdates } from './types'

export function NodeUpdatesWrapper() {
  return <WithTree Wrapped={NodeUpdates} />
}

export function NodeUpdates({
  tree,
  reFetchTree,
}: {
  tree: INode
  reFetchTree: () => void
}) {
  const [idMap, setIdMap] = useState<NodeIdMap>({})
  const [updates, setUpdates] = useState<NodeBasedUpdates[]>([])
  const [skipping, setSkipping] = useState<SkippedUpdates[]>([])
  const [selectedUpdate, setSelectedUpdate] = useState<
    NodeBasedUpdates | undefined
  >()
  const [disable, setDisable] = useState(false)

  useEffect(() => {
    getAllUpdates(tree).then(({ updates, skipping }) => {
      setUpdates(updates)
      setSkipping(skipping)
    })
    setIdMap(createIdMap(tree))
  }, [tree])

  function doUpdateAll() {
    setDisable(true)
    updateAll(...updates).then(reFetchTree)
  }
  function didUpdate(update: NodeBasedUpdates) {
    setUpdates((prev) => prev.filter((u) => u !== update))
    const node = update.node
    if (node) {
      setSkipping((prev) => [...prev, { node }])
    }
    setSelectedUpdate(undefined)
  }

  return (
    <Box>
      <Box>
        Nodes: {updates.length}{' '}
        <ConfirmCommitButton disabled={disable} onClick={doUpdateAll}>
          <>Nodes To Update (Press to update all)</>
        </ConfirmCommitButton>
      </Box>
      {selectedUpdate && (
        <Box sx={{ border: '1px solid', padding: '10px' }}>
          <UpdateView
            update={selectedUpdate}
            idMap={idMap}
            onDidUpdate={didUpdate}
          />
        </Box>
      )}
      <Box padding={'10px'}>
        <UpdatesList updates={updates} onSelect={setSelectedUpdate} />
      </Box>
      <Box padding={'10px'}>
        <SkippingList skipping={skipping} treeMap={idMap} />
      </Box>
    </Box>
  )
}

function UpdateView({
  update,
  idMap,
  onDidUpdate,
}: {
  update: NodeBasedUpdates
  idMap: NodeIdMap
  onDidUpdate: (u: NodeBasedUpdates) => void
}) {
  const [disable, setDisable] = useState(false)
  const { node, updates, extraInfo } = update
  const onClick = async () => {
    setDisable(true)
    await updateAll(update)
    onDidUpdate(update)
  }

  return (
    <Box>
      <Box>
        <Button onClick={() => node && openNode(node, idMap)}>
          {node?.title}
        </Button>
      </Box>
      {extraInfo && <Box>{extraInfo}</Box>}
      <Box>
        {updates.map(({ method, type, ...rest }, i) => (
          <Box
            key={`${method}${type}${i}`}
            padding={'2px'}
            border={'1px solid'}
          >
            <Box>
              <strong>{`${method} ${type}`}</strong>
            </Box>
            <TextareaAutosize
              value={JSON.stringify(rest, null, 2)}
              disabled={true}
              style={{ width: '100%' }}
            />
          </Box>
        ))}
        <Button disabled={disable || !updates.length} onClick={onClick}>
          Update
        </Button>
      </Box>
    </Box>
  )
}

function UpdatesList({
  updates,
  onSelect,
}: {
  updates: NodeBasedUpdates[]
  onSelect: (update: NodeBasedUpdates) => void
}) {
  return (
    <Box>
      {updates.map((update) => {
        const { node, extraInfo } = update
        return (
          <Box key={node?.id}>
            <Button onClick={() => onSelect(update)}>
              {node?.title || '[UNTITLED]'}
            </Button>
            {extraInfo}
          </Box>
        )
      })}
    </Box>
  )
}

function SkippingList({
  skipping,
  treeMap,
}: {
  skipping: SkippedUpdates[]
  treeMap: NodeIdMap
}) {
  if (!skipping.length) return null
  return (
    <Box>
      <Box>
        <strong>Already Done</strong>
      </Box>
      {skipping.map(({ node, extraInfo }) => {
        return (
          <Box key={node.id}>
            <Button onClick={() => openNode(node, treeMap)}>
              {node.title || '[UNTITLED]'}
            </Button>
            {extraInfo}
          </Box>
        )
      })}
    </Box>
  )
}
